import { ArrowUpRight } from "lucide-react";

import { CoachPhoto } from "../../components/coach-photo";
import { EmptyCard } from "../../components/empty-card";
import { GoLink } from "../../components/go-link";
import { PriceLines, priceLineOf } from "../../components/price-lines";
import { card, figure, label, peopleColumns, primaryOrb, well } from "../../components/section";
import { PageTitle, SiteHead } from "../../components/site-head";
import { SitePage } from "../../components/site-page";
import { TimeChip } from "../../components/time-chip";
import { usePublicDirectory } from "../../hooks/use-public-directory";
import { sitePath, useSiteConfig } from "../../hooks/use-site-config";
import type { PublicDirectory, PublicSpecialist, SlotPick } from "../../types";
import { nextOpenTime, openTimes } from "../../utils/directory";
import { coachAnchor, openTimeLabel } from "../../utils/labels";
import { SlotGateDialog } from "../open-calendar/slot-gate-dialog";
import { useSlotGate } from "../open-calendar/use-slot-gate";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingMarkdown } from "@/features/booking/modules/markdown/markdown";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Open times offered on each profile. */
const TIMES_PER_COACH = 5;

/** One coach's row of cards: who they are on the narrow side, what they do on the wide one. */
const profileGrid = "grid scroll-mt-3 gap-3 pt-10 first:pt-3 md:pt-16 lg:grid-cols-12";

type ProfileProps = {
  specialist: PublicSpecialist;
  directory: PublicDirectory;
  /** Every other profile puts its name card on the right. */
  flip: boolean;
  onPick: (pick: SlotPick) => void;
};

/**
 * One coach, as the trainer set them up in the booking pack: photo, title, bio, the services
 * they take and their open times.
 */
const Profile = ({ specialist, directory, flip, onPick }: ProfileProps) => {
  const { slug } = useSiteConfig();
  const next = nextOpenTime(specialist);
  const times = openTimes(specialist).slice(0, TIMES_PER_COACH);
  const offered = directory.services.filter((service) => specialist.serviceIds.includes(service.id));

  return (
    <article id={coachAnchor(specialist.id)} className={profileGrid}>
      <div className={cn(card, "flex flex-col lg:col-span-5", flip && "lg:order-2")}>
        <div className="flex items-center gap-[18px]">
          <CoachPhoto name={specialist.name} src={specialist.avatarUrl} className="size-[84px] rounded-3xl text-2xl" />
          <div className="min-w-0">
            <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] leading-[1.05] font-medium tracking-[-0.04em]">
              {specialist.name}
            </h2>
            {specialist.title ? <span className="text-[var(--site-ink)]">{specialist.title}</span> : null}
          </div>
        </div>

        {specialist.bio ? (
          <BookingMarkdown source={specialist.bio} className="mt-7 text-base text-muted-foreground" />
        ) : null}

        {next ? (
          <div
            className={cn(
              well,
              "mt-6 flex items-center justify-between gap-3 py-3 pr-3 pl-[18px] lg:mt-auto",
            )}
          >
            <div>
              <span className={label}>{m.services_fact_next()}</span>
              <div className={cn(figure, "mt-1 text-[22px]")}>{openTimeLabel(next, directory)}</div>
            </div>
            <button
              type="button"
              aria-label={m.landing_nav_book()}
              className={primaryOrb}
              onClick={() => onPick({ specialistId: specialist.id, serviceId: specialist.serviceId, startAt: next })}
            >
              <ArrowUpRight aria-hidden className="size-[18px]" />
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col gap-3 lg:col-span-7">
        {offered.length > 0 ? (
          <PriceLines lines={offered.map((service) => priceLineOf(service, directory.business.currency))} />
        ) : null}

        <div className={cn(card, "flex flex-1 flex-wrap items-center justify-between gap-4")}>
          <div className="flex flex-wrap gap-1.5">
            {times.map((startAt) => (
              <TimeChip
                key={startAt}
                onClick={() => onPick({ specialistId: specialist.id, serviceId: specialist.serviceId, startAt })}
              >
                {openTimeLabel(startAt, directory)}
              </TimeChip>
            ))}
          </div>
          <GoLink to={sitePath(slug)} hash="times">
            {m.coaches_full_week()}
          </GoLink>
        </div>
      </div>
    </article>
  );
};

/**
 * Every coach in full. The list is the booking pack's team, so a coach added under Setup shows
 * up here on their own; with nobody to book the page says so and gives the way to get in touch.
 */
export const Coaches = () => {
  const site = useSiteConfig();
  const { directory, loading } = usePublicDirectory({ days: site.calendarDays });
  const { open, gate } = useSlotGate(directory);
  const specialists = directory?.specialists ?? [];

  return (
    <SitePage
      head={
        <SiteHead>
          <PageTitle title={m.landing_nav_coaches()} />
          {loading || specialists.length > 0 ? (
            <nav className={cn("mt-9 grid gap-2", peopleColumns(loading ? 4 : specialists.length))}>
              {loading
                ? Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[60px] rounded-full" />)
                : specialists.map((specialist) => (
                    <a
                      key={specialist.id}
                      href={`#${coachAnchor(specialist.id)}`}
                      className="flex items-center gap-3 rounded-full bg-[var(--site-well)] py-2 pr-5 pl-2 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <CoachPhoto
                        name={specialist.name}
                        src={specialist.avatarUrl}
                        className="size-11 rounded-full text-xs"
                      />
                      <span className="min-w-0">
                        <b className="block truncate font-medium">{specialist.name}</b>
                        <small className="block truncate text-[13px] text-muted-foreground">{specialist.title}</small>
                      </span>
                    </a>
                  ))}
            </nav>
          ) : null}
        </SiteHead>
      }
    >
      {loading ? (
        <div className={profileGrid}>
          <Skeleton className="h-[420px] rounded-[28px] lg:col-span-5" />
          <div className="grid gap-3 lg:col-span-7">
            <Skeleton className="h-40 rounded-[28px]" />
            <Skeleton className="h-40 rounded-[28px]" />
            <Skeleton className="h-[72px] rounded-[28px]" />
          </div>
        </div>
      ) : null}

      {!loading && (!directory || specialists.length === 0) ? (
        <EmptyCard title={m.landing_calendar_empty_title()} />
      ) : null}

      {directory
        ? specialists.map((specialist, index) => (
            <Profile
              key={specialist.id}
              specialist={specialist}
              directory={directory}
              flip={index % 2 === 1}
              onPick={open}
            />
          ))
        : null}

      <SlotGateDialog {...gate} />
    </SitePage>
  );
};
